import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { AppLoader } from '@/components/AppLoader'
import { AdminRoute, ProtectedRoute, PublicOnlyRoute } from './guards'
import { PUBLIC_PATHS } from './publicPaths'

const SignupPage = lazy(() =>
  import('@/features/auth/pages/SignupPage').then((m) => ({ default: m.SignupPage })),
)
const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
)
const VerifyEmailPage = lazy(() =>
  import('@/features/auth/pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })),
)
const MagicLoginPage = lazy(() =>
  import('@/features/auth/pages/MagicLoginPage').then((m) => ({ default: m.MagicLoginPage })),
)
const UnsubscribePage = lazy(() =>
  import('@/features/unsubscribe/pages/UnsubscribePage').then((m) => ({ default: m.UnsubscribePage })),
)
const DeletionCancelPage = lazy(() =>
  import('@/features/account-deletion/pages/DeletionCancelPage').then((m) => ({
    default: m.DeletionCancelPage,
  })),
)
const DiscoveryPage = lazy(() =>
  import('@/features/prospect/pages/DiscoveryPage').then((m) => ({ default: m.DiscoveryPage })),
)
const ProspectUnsubscribePage = lazy(() =>
  import('@/features/prospect/pages/ProspectUnsubscribePage').then((m) => ({
    default: m.ProspectUnsubscribePage,
  })),
)
const AllListingsPage = lazy(() =>
  import('@/features/dashboard/pages/AllListingsPage').then((m) => ({ default: m.AllListingsPage })),
)
const ListingDetailPage = lazy(() =>
  import('@/features/dashboard/pages/ListingDetailPage').then((m) => ({ default: m.ListingDetailPage })),
)
const ComparePage = lazy(() =>
  import('@/features/dashboard/pages/ComparePage').then((m) => ({ default: m.ComparePage })),
)
const AccountPage = lazy(() =>
  import('@/features/subscriptions/pages/AccountPage').then((m) => ({ default: m.AccountPage })),
)
const SearchStatsPage = lazy(() =>
  import('@/features/stats/pages/SearchStatsPage').then((m) => ({ default: m.SearchStatsPage })),
)
const SearchesPage = lazy(() =>
  import('@/features/searches/pages/SearchesPage').then((m) => ({ default: m.SearchesPage })),
)
const SearchEditorPage = lazy(() =>
  import('@/features/searches/pages/SearchEditorPage').then((m) => ({ default: m.SearchEditorPage })),
)
const NotificationListingsPage = lazy(() =>
  import('@/features/notifications/pages/NotificationListingsPage').then((m) => ({
    default: m.NotificationListingsPage,
  })),
)
const ListsPage = lazy(() =>
  import('@/features/lists/pages/ListsPage').then((m) => ({ default: m.ListsPage })),
)
const ListDetailPage = lazy(() =>
  import('@/features/lists/pages/ListDetailPage').then((m) => ({ default: m.ListDetailPage })),
)
const AdminUsersPage = lazy(() =>
  import('@/features/admin/pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminUserDetailPage = lazy(() =>
  import('@/features/admin/pages/AdminUserDetailPage').then((m) => ({
    default: m.AdminUserDetailPage,
  })),
)
const AdminProspectsPage = lazy(() =>
  import('@/features/admin/pages/AdminProspectsPage').then((m) => ({
    default: m.AdminProspectsPage,
  })),
)
const AdminProspectDetailPage = lazy(() =>
  import('@/features/admin/pages/AdminProspectDetailPage').then((m) => ({
    default: m.AdminProspectDetailPage,
  })),
)
const AdminCampaignsPage = lazy(() =>
  import('@/features/admin/pages/AdminCampaignsPage').then((m) => ({
    default: m.AdminCampaignsPage,
  })),
)
const AdminOnboardingsPage = lazy(() =>
  import('@/features/admin/pages/AdminOnboardingsPage').then((m) => ({
    default: m.AdminOnboardingsPage,
  })),
)
const AdminTierSettingsPage = lazy(() =>
  import('@/features/admin/pages/AdminTierSettingsPage').then((m) => ({
    default: m.AdminTierSettingsPage,
  })),
)
const ImpersonationShell = lazy(() =>
  import('@/features/admin/components/ImpersonationShell').then((m) => ({
    default: m.ImpersonationShell,
  })),
)

function PageFallback() {
  return <AppLoader />
}

function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center flex-col gap-4">
      <h1 className="text-2xl font-semibold">Page introuvable</h1>
      <a href="/app/biens" className="text-primary hover:underline">
        Retour à l&apos;accueil
      </a>
    </div>
  )
}

/**
 * Stripe Checkout revient sur /app/account/subscription?checkout=success|cancel
 * (success_url/cancel_url côté back), mais la page canonique est /app/account.
 * On redirige en conservant le query string pour que AccountPage traite le retour.
 */
function AccountSubscriptionRedirect() {
  const { search } = useLocation()
  return <Navigate to={{ pathname: '/app/account', search }} replace />
}

export function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Navigate to="/app/biens" replace />} />

        <Route
          path={PUBLIC_PATHS.signup}
          element={
            <PublicOnlyRoute>
              <SignupPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path={PUBLIC_PATHS.login}
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route path={PUBLIC_PATHS.verifyEmail} element={<VerifyEmailPage />} />
        <Route path={PUBLIC_PATHS.magicLogin} element={<MagicLoginPage />} />
        {/* Public : désinscription via le token signé du lien email. Sous /app/
            comme les autres deep-links email (l'app n'est servie que sur /app/*
            en prod) ; pas de guard, doit fonctionner déconnecté. */}
        <Route path={PUBLIC_PATHS.unsubscribe} element={<UnsubscribePage />} />
        {/* Public : accès invité par lien signé (?token= strippé au premier rendu,
            gardé en mémoire uniquement). Sous /app/ comme les autres deep-links
            email (l'app n'est servie que sur /app/* en prod) ; aucun guard : le
            prospect n'a pas de compte. */}
        <Route path={PUBLIC_PATHS.discovery} element={<DiscoveryPage />} />
        {/* Public : désinscription prospect via token dédié (distinct du token
            d'accès et de l'unsubscribe digest abonné). Sous /app/ comme les
            autres deep-links email (l'app n'est servie que sur /app/* en prod). */}
        <Route path={PUBLIC_PATHS.prospectUnsubscribe} element={<ProspectUnsubscribePage />} />
        {/* Public : annulation de suppression via le token signé du lien email.
            Doit fonctionner déconnecté (sessions révoquées) — aucun guard.
            Aligné sur ALERTE_IMMO_DELETION_CANCEL_PATH (/app/account/deletion/cancel). */}
        <Route path={PUBLIC_PATHS.deletionCancel} element={<DeletionCancelPage />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/app/biens" element={<AllListingsPage />} />
          <Route path="/app/searches" element={<SearchesPage />} />
          <Route path="/app/searches/new" element={<SearchEditorPage />} />
          <Route path="/app/searches/:id/edit" element={<SearchEditorPage />} />
          <Route path="/app/lists" element={<ListsPage />} />
          <Route path="/app/lists/:id" element={<ListDetailPage />} />
          <Route path="/app/stats" element={<SearchStatsPage />} />
          <Route path="/app/listings/:id" element={<ListingDetailPage />} />
          <Route path="/app/notifications/:id" element={<NotificationListingsPage />} />
          <Route path="/app/compare" element={<ComparePage />} />
          <Route path="/app/account" element={<AccountPage />} />

          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <AdminUsersPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <AdminRoute>
                <AdminUserDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/prospects"
            element={
              <AdminRoute>
                <AdminProspectsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/prospects/:id"
            element={
              <AdminRoute>
                <AdminProspectDetailPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/campaigns"
            element={
              <AdminRoute>
                <AdminCampaignsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/onboardings"
            element={
              <AdminRoute>
                <AdminOnboardingsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/tier-settings"
            element={
              <AdminRoute>
                <AdminTierSettingsPage />
              </AdminRoute>
            }
          />
          {/* Vision client : pages app rendues en impersonation (lecture seule). */}
          <Route
            path="/admin/users/:userId/app"
            element={
              <AdminRoute>
                <ImpersonationShell />
              </AdminRoute>
            }
          >
            <Route index element={<Navigate to="biens" replace />} />
            <Route path="biens" element={<AllListingsPage />} />
            <Route path="searches" element={<SearchesPage />} />
            <Route path="lists" element={<ListsPage />} />
            <Route path="lists/:id" element={<ListDetailPage />} />
            <Route path="stats" element={<SearchStatsPage />} />
            <Route path="listings/:id" element={<ListingDetailPage />} />
            <Route path="compare" element={<ComparePage />} />
          </Route>
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Navigate to="/admin/users" replace />
              </AdminRoute>
            }
          />
        </Route>

        <Route path="/app/account/subscription" element={<AccountSubscriptionRedirect />} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
